import React, { Component } from 'react'
import { connect } from 'react-redux'
import Input from '../../components/inputs/input-simple'
import Button from '../../components/buttons/buttons'
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { getProjectList } from '../../actions/project-management.action'
import { bindActionCreators } from 'redux'
import { warningSnackBar } from '../../actions/common.action'
import _ from 'lodash'
import moment from 'moment'
import { sortByKeyAsc } from '../../util/common'
import './scss/timesheet.scss'

/**
 * Filter controls bar for Timesheet views. Provides project type, date range,
 * worker selection, and quick period filtering actions.
 *
 * @extends {Component}
 */
export class Filter extends Component {

  constructor(props) {
    super(props)
    this.state = {
      fields: {
        projectType: {
          type: 'select',
          items: [],
          value: 'all'
        },
        project: {
          type: 'select',
          items: [],
          value: 'all'
        },
        resource: {
          type: 'select',
          items: [],
          value: 'all'
        },
        status: {
          type: 'select',
          items: [],
          value: 'all'
        },
        confirmationStatus: {
          type: 'select',
          items: [],
          value: 'all'
        },
        createdFrom: {
          type: 'select',
          items: [],
          value: 'all'
        },
        startDate: {
          type: 'datepicker',
          className: 'timesheet-filter-datepicker',
          value: ''
        },
        endDate: {
          type: 'datepicker',
          className: 'timesheet-filter-datepicker',
          value: ''
        }
      }
    }
  }

  async componentDidMount() {
    const {
      projectCategories,
      dashboardFilter,
      timesheetFilter,
      showResourceFilter,
      showStatusFilter,
      resourceOptions = [],
      statusOptions = [],
    } = this.props;
    const user = this.props.user || {};
    const permissions = user.permissions || {};
    // Restore persisted filter settings
    const saved = JSON.parse(sessionStorage.getItem('timesheetFilters') || '{}');

    const fields = _.cloneDeep(this.state.fields);

    // Build project type options
    const typeOptions = [];
    if (permissions.dashboardProjects === 2 || user.global_user) {
      projectCategories.forEach(cat => {
        if (permissions[`projectManagement${cat.type}`] > 0 || (user.global_user && user.selected_account.id)) {
          typeOptions.push({ key: cat.name, value: cat.type });
        }
      });
    }
    if (typeOptions.length > 1) typeOptions.unshift({ key: 'All', value: 'all' });
    fields.projectType.items = typeOptions;
    fields.projectType.value = saved.projectType && typeOptions.find(o => o.value === saved.projectType)
      ? saved.projectType
      : typeOptions[0]?.value || '';

    const filterType = fields.projectType.value === 'all' ? null : fields.projectType.value;
    let projects = [];
    if (filterType || fields.projectType.value === 'all') {
      projects = await this.props.getProjectList(null, 'PROJECT', null, filterType, null, null);
    }
    if (typeof this.props.onProjectsLoaded === 'function' && Array.isArray(projects)) {
      this.props.onProjectsLoaded(projects);
    }
    let projectItems = Array.isArray(projects)
      ? projects.map(p => ({ key: p.name || p.description, value: p.id }))
      : [];
    projectItems.unshift({ key: 'All', value: 'all' });
    projectItems = sortByKeyAsc(projectItems);
    fields.project.items = projectItems;
    fields.project.value = saved.project && projectItems.find(o => o.value === saved.project)
      ? saved.project
      : 'all';

    if (showResourceFilter) {
      const resourceItems = Array.isArray(resourceOptions) && resourceOptions.length
        ? resourceOptions
        : [{ key: 'All', value: 'all' }];
      fields.resource.items = resourceItems;
      fields.resource.value = saved.resource && resourceItems.find(o => o.value === saved.resource)
        ? saved.resource
        : 'all';
    }

    if (showStatusFilter) {
      const defaultStatusItems = statusOptions.length ? statusOptions : [
        { key: 'All', value: 'all' },
        { key: 'Pending', value: 'PENDING' },
        { key: 'Approved', value: 'APPROVED' },
        { key: 'Rejected', value: 'REJECTED' },
      ];
      fields.status.items = defaultStatusItems;
      fields.status.value = saved.status && defaultStatusItems.find(o => o.value === saved.status)
        ? saved.status
        : (defaultStatusItems.find(o => o.value === 'PENDING')?.value || defaultStatusItems[0].value);
    }

    const confirmationOptions = [
      { key: 'All', value: 'all' },
      { key: 'Confirmed', value: 'confirmed' },
      { key: 'Unconfirmed', value: 'unconfirmed' },
    ];
    fields.confirmationStatus.items = confirmationOptions;
    fields.confirmationStatus.value = saved.confirmationStatus && confirmationOptions.find(o => o.value === saved.confirmationStatus)
      ? saved.confirmationStatus
      : 'all';

    const createdFromOptions = [
      { key: 'All', value: 'all' },
      { key: 'Web', value: 'web' },
      { key: 'Mobile', value: 'mobile' },
    ];
    fields.createdFrom.items = createdFromOptions;
    fields.createdFrom.value = saved.createdFrom && createdFromOptions.find(o => o.value === saved.createdFrom)
      ? saved.createdFrom
      : 'all';

    // Set date window (UTC calendar days): current month or current week, then override if saved
    const now = new Date();
    let start = new Date(), end = new Date();
    if (dashboardFilter) {
      start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    } else if (timesheetFilter) {
      const d = now.getUTCDay();
      start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - (d === 0 ? 6 : d - 1)));
    }
    end.setUTCHours(23, 59, 59, 999);
    start.setUTCHours(0, 0, 0, 0);
    if (saved.startDate) start = new Date(saved.startDate);
    if (saved.endDate) end = new Date(saved.endDate);
    fields.startDate.value = start;
    fields.endDate.value = end;

    this.setState({ fields });
    this.props.applyFilter(fields);
  }

  componentDidUpdate(prevProps) {
    const { showResourceFilter, resourceOptions = [], showStatusFilter, statusOptions = [] } = this.props;
    const fields = _.cloneDeep(this.state.fields);
    let updated = false;

    if (showResourceFilter && !_.isEqual(prevProps.resourceOptions, resourceOptions)) {
      const resourceItems = Array.isArray(resourceOptions) && resourceOptions.length
        ? resourceOptions
        : [{ key: 'All', value: 'all' }];
      fields.resource.items = resourceItems;
      if (!resourceItems.find(item => item.value === fields.resource.value)) {
        fields.resource.value = 'all';
      }
      updated = true;
    }

    if (showStatusFilter && !_.isEqual(prevProps.statusOptions, statusOptions)) {
      const defaultStatusItems = statusOptions.length ? statusOptions : [
        { key: 'All', value: 'all' },
        { key: 'Pending', value: 'PENDING' },
      ];
      fields.status.items = defaultStatusItems;
      if (!defaultStatusItems.find(item => item.value === fields.status.value)) {
        fields.status.value = defaultStatusItems.find(o => o.value === 'PENDING')?.value || defaultStatusItems[0].value;
      }
      updated = true;
    }

    if (updated) {
      this.setState({ fields });
    }
  }

  projectTypeChangeHandler = async (value, initialProjectValue) => {
    const { projectCategories } = this.props;
    // const { managerId } = this.props // this is added for managerId filter for listing projects/services
    let list
    if (projectCategories.find(o => o.type === value)) {
      list = await this.props.getProjectList(null, 'PROJECT', null, value, null, null)
    } else {
      list = []
    }
    if (typeof this.props.onProjectsLoaded === 'function' && Array.isArray(list)) {
      this.props.onProjectsLoaded(list);
    }

    const listItems = (Array.isArray(list) && list.map(({ id, name, description }) => ({
      key: name || description,
      value: id
    }))) || []

    listItems.unshift({ key: 'All', value: 'all' })

    const fields = _.cloneDeep(this.state.fields)
    fields.project.items = listItems
    fields.projectType.value = value
    // apply saved project value if provided
    if (initialProjectValue && listItems.find(item => item.value === initialProjectValue)) {
      fields.project.value = initialProjectValue
    } else {
      fields.project.value = 'all'
    }
    this.setState({ fields })
  }

  projectChangeHandler = (value) => {

    const fields = _.cloneDeep(this.state.fields)
    fields.project.value = value

    this.setState({ fields })
  }

  dateChangeHandler = (value, type) => {

    const fields = _.cloneDeep(this.state.fields)
    let date = new Date(value)

    if (type === 'startDate') {
      date.setHours(0, 0, 0, 0)
      fields.startDate.value = value
    } else if (type === 'endDate') {
      date.setHours(23, 59, 59, 999)
      fields.endDate.value = value
    }

    this.setState({ fields })
  }

  applyFilter = () => {
    const fields = _.cloneDeep(this.state.fields)

    // persist selected filters to sessionStorage
    const saved = {
      projectType: fields.projectType.value,
      project: fields.project.value,
      resource: fields.resource?.value || 'all',
      status: fields.status?.value || 'all',
      confirmationStatus: fields.confirmationStatus?.value || 'all',
      createdFrom: fields.createdFrom?.value || 'all',
      startDate: fields.startDate.value ? fields.startDate.value.toString() : null,
      endDate: fields.endDate.value ? fields.endDate.value.toString() : null,
    }
    sessionStorage.setItem('timesheetFilters', JSON.stringify(saved));

    if (fields.startDate.value) {
      fields.startDate.value = moment(fields.startDate.value).utc().toString()
    }
    if (fields.endDate.value) {
      fields.endDate.value = moment(fields.endDate.value).utc().toString()
    }

    if (fields.startDate.value && fields.endDate.value && moment(fields.startDate.value).isAfter(fields.endDate.value)) {
      this.props.warningSnackBar('End date can not be before start date')
      return
    }
    this.props.applyFilter(fields)
  }

  /**
   * Shifts date filters to the previous week (Monday to Sunday, UTC).
   */
  applyPreviousWeek = () => {
    const fields = _.cloneDeep(this.state.fields);
    const date = fields.startDate.value ? new Date(fields.startDate.value) : new Date();

    const day = date.getUTCDay();
    const prevMonday = new Date(date);
    if (day === 0) {
      prevMonday.setUTCDate(date.getUTCDate() - 6);
    } else if (day === 1) {
      prevMonday.setUTCDate(date.getUTCDate() - 7);
    } else {
      prevMonday.setUTCDate(date.getUTCDate() - (day - 1) - 7);
    }

    const startDate = new Date(prevMonday);
    const endDate = new Date(prevMonday.setUTCDate(prevMonday.getUTCDate() + 6));
    endDate.setUTCHours(23, 59, 59, 999);
    startDate.setUTCHours(0, 0, 0, 0);
    fields.startDate.value = startDate;
    fields.endDate.value = endDate;

    this.setState({ fields }, () => this.applyFilter());
  }

  render() {
    const { fields } = this.state
    const { showReportButton, timesheetFilter, showResourceFilter, showStatusFilter, extraActions } = this.props
    return (
      <Accordion data-cy="timesheet-filter-accordion">
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel1a-content'
          id='panel1a-header'
        >
          <b>Apply Filter</b>
        </AccordionSummary>
        <AccordionDetails style={{ flexDirection: 'column' }}>
          <Grid container style={{ textAlign: "left" }} className="subtaskrow-1">
            {/* Row 1: Project Type & Select Project */}
            <Grid md={6} xs={12} item>
              <Grid container alignItems="center">
                <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Project Type</p></Grid>
                <Grid md={6} sm={9} xs={12} item>
                  <Input
                    data-cy="timesheet-filter-project-type-input"
                    {...fields.projectType}
                    onChange={(e) => this.projectTypeChangeHandler(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid md={6} xs={12} item>
              <Grid container alignItems="center">
                <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Select Project</p></Grid>
                <Grid md={6} sm={9} xs={12} item>
                  <Input
                    data-cy="timesheet-filter-project-input"
                    {...fields.project}
                    onChange={(e) => this.projectChangeHandler(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Row 2: Resource & Status */}
            {showResourceFilter && (
              <Grid md={6} xs={12} item>
                <Grid container alignItems="center">
                  <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Resource</p></Grid>
                  <Grid md={6} sm={9} xs={12} item>
                    <Input
                      data-cy="timesheet-filter-resource-input"
                      {...fields.resource}
                      onChange={(e) => this.setState({
                        fields: {
                          ...this.state.fields,
                          resource: { ...fields.resource, value: e.target.value }
                        }
                      })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {showStatusFilter && (
              <Grid md={6} xs={12} item>
                <Grid container alignItems="center">
                  <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Status</p></Grid>
                  <Grid md={6} sm={9} xs={12} item>
                    <Input
                      data-cy="timesheet-filter-status-input"
                      {...fields.status}
                      onChange={(e) => this.setState({
                        fields: {
                          ...this.state.fields,
                          status: { ...fields.status, value: e.target.value }
                        }
                      })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Row 3: Confirmation Status & Submission Source */}
            {timesheetFilter && (
              <Grid md={6} xs={12} item>
                <Grid container alignItems="center">
                  <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Confirmation Status</p></Grid>
                  <Grid md={6} sm={9} xs={12} item>
                    <Input
                      data-cy="timesheet-filter-confirmation-input"
                      {...fields.confirmationStatus}
                      onChange={(e) => this.setState({
                        fields: {
                          ...this.state.fields,
                          confirmationStatus: { ...fields.confirmationStatus, value: e.target.value }
                        }
                      })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {timesheetFilter && (
              <Grid md={6} xs={12} item>
                <Grid container alignItems="center">
                  <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Submission Source</p></Grid>
                  <Grid md={6} sm={9} xs={12} item>
                    <Input
                      data-cy="timesheet-filter-source-input"
                      {...fields.createdFrom}
                      onChange={(e) => this.setState({
                        fields: {
                          ...this.state.fields,
                          createdFrom: { ...fields.createdFrom, value: e.target.value }
                        }
                      })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Row 4 (Last Row): Start Date & End Date */}
            <Grid md={6} xs={12} item>
              <Grid container alignItems="center">
                <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>Start Date</p></Grid>
                <Grid md={6} sm={9} xs={12} item>
                  <Input
                    data-cy="timesheet-filter-start-date-input"
                    className="timesheet-filter-datepicker"
                    {...fields.startDate}
                    onChange={(value) => this.dateChangeHandler(value, 'startDate')}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid md={6} xs={12} item>
              <Grid container alignItems="center">
                <Grid md={4} sm={3} xs={12} item> <p style={{ margin: 0 }}>End Date</p></Grid>
                <Grid md={6} sm={9} xs={12} item>
                  <Input
                    data-cy="timesheet-filter-end-date-input"
                    className="timesheet-filter-datepicker"
                    {...fields.endDate}
                    onChange={(value) => this.dateChangeHandler(value, 'endDate')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {extraActions && (
            <div className="mt-3">
              {extraActions}
            </div>
          )}
          <div className="timesheetFilters">
            {showReportButton && <Button className="btnPrimary" title={'Generate Payroll Report'} onClick={this.props.generateReport} />}
            <Button className="btnPrimary" title={'Apply Filter'} onClick={() => this.applyFilter(fields)} data-cy="timesheet-filter-apply-button" />
            {timesheetFilter && <Button className="btnPrimary" title={'Previous Week'} onClick={this.applyPreviousWeek} data-cy="timesheet-filter-previous-week-button" />}
          </div>
        </AccordionDetails>
      </Accordion>
    )
  }
}

const mapStateToProps = (state) => ({
  user: state.user.userData,
  projectCategories: state.project.projectCategories,
})

const mapDispatchToProps = dispatch => ({
  getProjectList: bindActionCreators(getProjectList, dispatch),
  warningSnackBar: bindActionCreators(warningSnackBar, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(Filter)
